import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/stores/authStore';
import { apiClient } from '@/lib/api';
import { AnkhSpinner } from '@/components/common/AnkhSpinner';
import { DashboardBackdrop } from '@/components/common/DashboardBackdrop';
import { isSessionAlive } from '@/lib/session-liveness';
import { shouldGateToApp } from '@/lib/platform';
import { clearGameSessionState } from '@/lib/clear-game-session-state';
import { toast } from '@/lib/toast';

const ACTIVE_SESSION_KEY = 'active-game-session';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute - Handles authentication and session routing
 *
 * Flow:
 * 1. Wait for zustand rehydration AND Firebase auth to complete
 * 2. Check auth state - redirect to /login if not authenticated
 * 3. Check localStorage for active game session (instant)
 * 4. If not in localStorage, check API (fast query)
 * 5. Route to game if active session, otherwise to lobby
 *
 * IMPORTANT: We must wait for BOTH hasHydrated AND !authLoading before making
 * any routing decisions to prevent flicker between login/lobby/game.
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isLoggedIn, isLoading: authLoading, hasHydrated } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  // Session check state
  const [sessionCheckState, setSessionCheckState] = useState<'idle' | 'checking' | 'done'>('idle');
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  // Ref to track if we've already initiated a redirect (prevents duplicate redirects during React's render cycles)
  const hasInitiatedRedirect = useRef(false);

  const isOnGameRoute = location.pathname.startsWith('/play/');
  const isOnLobbyRoute = location.pathname === '/lobby';
  const isOnAllowedRoute =
    location.pathname === '/dashboard' ||
    location.pathname === '/buy-tokens' ||
    location.pathname === '/admin';

  // Reset redirect guard when location changes (allows new redirects after navigation completes)
  useEffect(() => {
    hasInitiatedRedirect.current = false;
  }, [location.pathname]);

  // Check for active session when authenticated
  useEffect(() => {
    // Wait for both hydration and auth to complete before checking session
    if (!hasHydrated || authLoading || !isLoggedIn) {
      return;
    }

    // Use AbortController for proper request cancellation on unmount
    // Note: Don't check sessionCheckState here - StrictMode aborts then re-runs effects
    // with preserved state, so we'd skip the re-run after cleanup aborts the request
    const abortController = new AbortController();
    const signal = abortController.signal;
    // Cancellation flag for async .then callbacks. Belt-and-suspenders pairing
    // with the abort signal: in React 18, microtasks (where .then resolves)
    // can drain between a navigate-triggering click and React's re-render-with-
    // cleanup. The dead-session branch on /play/<id> calls navigate('/lobby'),
    // which would override the user's intentional /play/<otherId> navigation
    // if the stale .then runs after the click but before cleanup. The flag is
    // set synchronously in the cleanup return, so even if the cleanup races
    // the microtask, the second-line check catches it.
    let cancelled = false;

    // On /play/<id>: verify the session is alive before letting GameLayout mount.
    // Closes the post-login bounce (login → state.from → /play/<dead-id> → WS 4004 → /lobby).
    if (isOnGameRoute) {
      const sessionIdFromPath = location.pathname.split('/')[2] || null;
      if (!sessionIdFromPath) {
        setSessionCheckState('done');
        return () => {
          cancelled = true;
          abortController.abort();
        };
      }

      setSessionCheckState('checking');
      isSessionAlive(sessionIdFromPath, { signal }).then((alive) => {
        if (cancelled || signal.aborted) return;
        if (alive) {
          // Keep localStorage in sync with the URL so a refresh on this tab
          // doesn't lose the active-session marker.
          localStorage.setItem(ACTIVE_SESSION_KEY, sessionIdFromPath);
          setActiveSessionId(sessionIdFromPath);
          setSessionCheckState('done');
          return;
        }
        console.log('[ProtectedRoute] Session no longer exists, routing to lobby', {
          sessionIdFromPath,
        });
        clearGameSessionState('route-hints');
        setActiveSessionId(null);
        setSessionCheckState('done');
        toast.info('Session Ended', {
          description: 'This game session has ended. Returning to lobby...',
        });
        navigate('/lobby', { replace: true });
      });
      return () => {
        cancelled = true;
        abortController.abort();
      };
    }

    // Guard: if a session just ended (set by GameConnectionManager on WS close 4100),
    // skip all session checks and go straight to lobby. This prevents a redirect loop
    // where the API server or game server still reports a stale active session during
    // the brief window between session end and Firestore/DashMap cleanup.
    // NOTE: Only remove the flag INSIDE the TTL check so StrictMode double-renders
    // still see it on the second invocation.
    const justEnded = sessionStorage.getItem('session-just-ended');
    if (justEnded) {
      const elapsed = Date.now() - parseInt(justEnded, 10);
      if (elapsed < 30_000) {
        // Do NOT removeItem here — leave it so StrictMode's second render
        // also takes the fast-path. It will expire naturally at 30s.
        console.log('[ProtectedRoute] Session just ended, skipping active session check');
        clearGameSessionState('route-hints');
        setActiveSessionId(null);
        setSessionCheckState('done');
        return () => abortController.abort();
      }
      // Stale flag (>30s) — remove and fall through to normal check
      sessionStorage.removeItem('session-just-ended');
    }

    // Check localStorage first (instant)
    const localSession = localStorage.getItem(ACTIVE_SESSION_KEY);
    if (localSession) {
      console.log('[ProtectedRoute] Found session in localStorage, validating...', {
        localSession,
      });
      setSessionCheckState('checking');

      isSessionAlive(localSession, { signal }).then((alive) => {
        if (signal.aborted) return;
        if (alive) {
          console.log('[ProtectedRoute] Session validated, redirecting to game', {
            localSession,
          });
          setActiveSessionId(localSession);
        } else {
          console.log('[ProtectedRoute] Session no longer exists, clearing localStorage', {
            localSession,
          });
          clearGameSessionState('route-hints');
          setActiveSessionId(null);
        }
        setSessionCheckState('done');
      });
      return () => abortController.abort();
    }

    // No localStorage session - check lobby server API for active session
    setSessionCheckState('checking');

    apiClient
      .getActiveSession()
      .then(async ({ activeSessionId: serverSessionId }) => {
        if (signal.aborted) return;
        if (serverSessionId) {
          console.log('[ProtectedRoute] Server returned active session, validating...', {
            serverSessionId,
          });
          const alive = await isSessionAlive(serverSessionId, { signal });
          if (signal.aborted) return;
          if (alive) {
            console.log('[ProtectedRoute] Session validated, redirecting to game', {
              serverSessionId,
            });
            localStorage.setItem(ACTIVE_SESSION_KEY, serverSessionId);
            setActiveSessionId(serverSessionId);
          } else {
            console.log('[ProtectedRoute] Session no longer exists, routing to lobby', {
              serverSessionId,
            });
            clearGameSessionState('route-hints');
            setActiveSessionId(null);
          }
        } else {
          console.log('[ProtectedRoute] No active session, routing to lobby');
          clearGameSessionState('route-hints');
          setActiveSessionId(null);
        }
        setSessionCheckState('done');
      })
      .catch((error) => {
        if (signal.aborted) return;
        console.error('[ProtectedRoute] Failed to check active session', error);
        // On error, clear localStorage and proceed to lobby
        clearGameSessionState('route-hints');
        setActiveSessionId(null);
        setSessionCheckState('done');
      });

    return () => abortController.abort();
  }, [hasHydrated, isLoggedIn, authLoading, isOnGameRoute, location.pathname, navigate]);

  // Dashboard-bound loaders render the dashboard backdrop so the user never
  // sees a blank bg-background flash on the `/` → `/dashboard` redirect path.
  // Non-dashboard protected routes (lobby, play, etc.) have their own loading
  // UX, so a bare spinner is the right neutral state there.
  const isOnDashboard = location.pathname === '/dashboard';
  // Hoist DashboardBackdrop above the loading/loaded conditional so the
  // <video> element keeps the same React position across the swap. Without
  // this, the loader's wrapper and DashboardPage's wrapper are different
  // subtrees → React unmounts and remounts the backdrop → the new <video>
  // restarts its load cycle, flashing the bg-background through.
  const dashboardBackdrop = isOnDashboard ? <DashboardBackdrop /> : null;
  const renderLoading = () =>
    isOnDashboard ? (
      <div className="relative z-[3] flex items-center justify-center min-h-screen">
        <AnkhSpinner size="lg" className="text-accent-gold" />
      </div>
    ) : (
      <div className="flex items-center justify-center min-h-screen">
        <AnkhSpinner size="lg" className="text-accent-gold" />
      </div>
    );

  // Loading states - wait for BOTH zustand rehydration AND Firebase auth
  // This prevents route flicker between login/lobby/game during initialization
  if (!hasHydrated || authLoading) {
    return (
      <>
        {dashboardBackdrop}
        {renderLoading()}
      </>
    );
  }

  // Not logged in - redirect to login
  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Phones/tablets never reach the web game surfaces: lobby and play deep
  // links (including the guest signup's post-auth /lobby redirect) go to the
  // install-store page instead. The dashboard stays reachable on mobile for
  // account actions; its own enter-lobby buttons apply the same gate.
  if ((isOnLobbyRoute || isOnGameRoute) && shouldGateToApp()) {
    return <Navigate to="/get-the-app" replace />;
  }

  // Email verification is optional — Firebase verification emails reliably
  // land in spam, so gating on emailVerified strands users on /verify-email
  // with no recovery. Users can still verify later from account settings; the
  // EmailSection surfaces a "Verify now" affordance for that path.

  // Checking session
  if (sessionCheckState === 'idle' || sessionCheckState === 'checking') {
    return (
      <>
        {dashboardBackdrop}
        {renderLoading()}
      </>
    );
  }

  // Session check done - route appropriately
  // When redirect is needed, ALWAYS return Navigate (not children) to prevent child effects from firing
  // Use ref guard only for logging to avoid duplicate console messages
  //
  // Render-time `session-just-ended` guard: when GameLayout calls
  // `navigate('/lobby')` after a session ends, it has already set the
  // `session-just-ended` sessionStorage flag. But the effect that processes
  // this flag (line 124) runs AFTER commit, and on the first render with
  // the new `/lobby` pathname `activeSessionId` STATE is still the
  // just-ended session id from the previous /play render — so this block
  // would fire `<Navigate to=/play/{deadId}>` and bounce the player back
  // to /play for one render cycle. The next render sees the effect's
  // `setActiveSessionId(null)` and bounces back to /lobby. The bounce is
  // cosmetic but produces "WebSocket closed before connection established"
  // errors as the lobby WS opens, gets killed by the unmount, opens again.
  // Reading the flag at render time short-circuits the bogus redirect.
  const justEndedRaw = sessionStorage.getItem('session-just-ended');
  const justEnded = justEndedRaw !== null && Date.now() - parseInt(justEndedRaw, 10) < 30_000;
  if (activeSessionId && !isOnGameRoute && !isOnAllowedRoute && !justEnded) {
    if (!hasInitiatedRedirect.current) {
      hasInitiatedRedirect.current = true;
      console.log('[ProtectedRoute] Redirecting to game', { activeSessionId });
    }
    return <Navigate to={`/play/${activeSessionId}`} replace />;
  }

  if (!activeSessionId && !isOnLobbyRoute && !isOnGameRoute && !isOnAllowedRoute) {
    if (!hasInitiatedRedirect.current) {
      hasInitiatedRedirect.current = true;
      console.log('[ProtectedRoute] Redirecting to dashboard');
    }
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      {dashboardBackdrop}
      {children}
    </>
  );
}
